<template>
  <Base>
    <template #toolbar>
      <div class="toolbar" id="kt_toolbar">
        <div class="container-fluid d-flex flex-stack" id="kt_toolbar_container">
          <div class="d-flex align-items-center me-3">
            <span class="h-20px border-gray-200 border-start mx-4"></span>
            <ul class="breadcrumb breadcrumb-separatorless fw-bold fs-7 my-1">
              <li class="breadcrumb-item text-muted">
                <router-link :to="{ name: 'dashboard' }" class="text-muted text-hover-primary">
                  <i class="fad fa-home"></i> Tableau de bord
                </router-link>
              </li>
              <li class="breadcrumb-item"><span class="bullet bg-gray-200 w-5px h-2px"></span></li>
              <li class="breadcrumb-item text-dark">Providers IA</li>
            </ul>
          </div>
          <div class="d-flex align-items-center py-1">
            <button class="btn btn-primary btn-sm" @click="resetForm" v-b-modal="'providerModal'">
              <i class="fad fa-plus me-1"></i> Nouveau provider
            </button>
          </div>
        </div>
      </div>
    </template>
    <template #content>
      <div class="card">
        <div class="card-body">
          <div v-if="providers.length === 0" class="text-center text-muted py-10">
            <i class="fad fa-robot fs-3x mb-3 d-block"></i>
            Aucun provider IA configure. Ajoutez-en un pour activer l'IA conversationnelle.
          </div>
          <div class="table-responsive" v-else>
            <table class="table table-striped align-middle">
              <thead>
                <tr class="fw-bolder text-muted">
                  <th>Nom</th>
                  <th>Type</th>
                  <th>Modele</th>
                  <th>URL</th>
                  <th class="text-center">Par defaut</th>
                  <th class="text-center">Actif</th>
                  <th class="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in providers" :key="p.id">
                  <td class="fw-bolder">{{ p.label }}</td>
                  <td>
                    <span class="badge" :class="typeBadge(p.type)">{{ p.type }}</span>
                  </td>
                  <td class="text-muted">{{ p.model }}</td>
                  <td class="text-muted fs-8">{{ p.baseUrl }}</td>
                  <td class="text-center">
                    <i v-if="p.isDefault" class="fad fa-star text-warning"></i>
                  </td>
                  <td class="text-center">
                    <span v-if="p.isActive" class="badge badge-light-success">Actif</span>
                    <span v-else class="badge badge-light-danger">Inactif</span>
                  </td>
                  <td class="text-end">
                    <button class="btn btn-sm btn-light-primary me-1" @click="testProvider(p)" :disabled="testing === p.id">
                      <i class="fad fa-flask me-1"></i>
                      <template v-if="testing === p.id">Test...</template>
                      <template v-else>Tester</template>
                    </button>
                    <button class="btn btn-sm btn-light-warning me-1" @click="editProvider(p)" v-b-modal="'providerModal'">
                      <i class="fad fa-edit"></i>
                    </button>
                    <button class="btn btn-sm btn-light-danger" @click="deleteProvider(p)">
                      <i class="fad fa-trash"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Resultat du test -->
          <div v-if="testResult" class="mt-5 p-5 rounded" :class="testResult.success ? 'bg-light-success' : 'bg-light-danger'">
            <div class="fw-bolder mb-2">
              <i :class="testResult.success ? 'fad fa-check text-success' : 'fad fa-times text-danger'" class="me-2"></i>
              {{ testResult.success ? 'Connexion reussie' : 'Echec de connexion' }}
            </div>
            <div v-if="testResult.latency" class="text-muted">Latence : {{ testResult.latency }}ms</div>
            <div v-if="testResult.error" class="text-danger">{{ testResult.error }}</div>
            <div v-if="testResult.response" class="text-muted fs-8 mt-2">Reponse : {{ testResult.response }}</div>
          </div>
        </div>
      </div>

      <!-- Modal Create/Edit -->
      <b-modal id="providerModal" :title="form.id ? 'Modifier le provider' : 'Nouveau provider'" centered size="lg" @ok.prevent="saveProvider">
        <div class="row">
          <div class="col-12 mb-3">
            <label class="required form-label">Nom</label>
            <b-form-input v-model="form.label" placeholder="Ex: Ollama local, Claude Production"></b-form-input>
          </div>
          <div class="col-6 mb-3">
            <label class="required form-label">Type</label>
            <b-form-select v-model="form.type" :options="typeOptions" @change="onTypeChange"></b-form-select>
          </div>
          <div class="col-6 mb-3">
            <label class="required form-label">Modele</label>
            <b-form-input v-model="form.model" :placeholder="modelPlaceholder"></b-form-input>
            <div v-if="ollamaModels.length > 0" class="mt-1">
              <span v-for="m in ollamaModels" :key="m.name" class="badge badge-light-primary me-1 cursor-pointer" @click="form.model = m.name">
                {{ m.name }}
              </span>
            </div>
          </div>
          <div class="col-12 mb-3">
            <label class="required form-label">URL</label>
            <b-form-input v-model="form.baseUrl" placeholder="https://..."></b-form-input>
          </div>
          <div class="col-12 mb-3" v-if="needsApiKey">
            <label class="form-label">Cle API</label>
            <b-form-input v-model="form.apiKey" type="password" placeholder="sk-... ou similaire"></b-form-input>
          </div>
          <div class="col-4 mb-3">
            <label class="form-label">Temperature</label>
            <b-form-input v-model.number="form.temperature" type="number" step="0.1" min="0" max="2"></b-form-input>
          </div>
          <div class="col-4 mb-3">
            <label class="form-label">Max tokens</label>
            <b-form-input v-model.number="form.maxTokens" type="number"></b-form-input>
          </div>
          <div class="col-4 mb-3">
            <label class="form-label">Timeout (s)</label>
            <b-form-input v-model.number="form.timeout" type="number"></b-form-input>
          </div>
          <div class="col-6 mb-3">
            <b-form-checkbox v-model="form.isActive">Actif</b-form-checkbox>
          </div>
          <div class="col-6 mb-3">
            <b-form-checkbox v-model="form.isDefault">Provider par defaut</b-form-checkbox>
          </div>
        </div>
      </b-modal>
    </template>
  </Base>
</template>

<script lang="ts">
import {Component, Vue} from 'vue-property-decorator';
import Base from "@/layouts/Base.vue";
import {api} from "@/services/Api";

@Component({components: {Base}})
export default class AiProviderManager extends Vue {
  providers: any[] = []
  form: any = {}
  testing: any = null
  testResult: any = null
  ollamaModels: any[] = []

  typeOptions = [
    {value: 'OLLAMA_LOCAL', text: 'Ollama (local)'},
    {value: 'OLLAMA_REMOTE', text: 'Ollama (distant)'},
    {value: 'CLAUDE', text: 'Claude (Anthropic)'},
    {value: 'OPENAI', text: 'OpenAI'},
    {value: 'CUSTOM', text: 'Custom (compatible OpenAI)'},
  ]

  get needsApiKey() {
    return ['CLAUDE', 'OPENAI', 'CUSTOM'].includes(this.form.type)
  }

  get modelPlaceholder() {
    const map: any = {
      OLLAMA_LOCAL: 'mistral:7b',
      OLLAMA_REMOTE: 'mistral:7b',
      CLAUDE: 'claude-sonnet-4-20250514',
      OPENAI: 'gpt-4o-mini',
      CUSTOM: 'model-name',
    }
    return map[this.form.type] || 'nom du modele'
  }

  typeBadge(type: string) {
    const map: any = {
      OLLAMA_LOCAL: 'badge-light-info',
      OLLAMA_REMOTE: 'badge-light-info',
      CLAUDE: 'badge-light-warning',
      OPENAI: 'badge-light-success',
      CUSTOM: 'badge-light-primary',
    }
    return map[type] || 'badge-light-secondary'
  }

  mounted() {
    this.loadProviders()
  }

  resetForm() {
    this.form = {
      label: '', type: 'OLLAMA_LOCAL', baseUrl: 'http://localhost:11434',
      apiKey: '', model: 'mistral:7b', temperature: 0.3,
      maxTokens: 1024, timeout: 30, isActive: true, isDefault: false
    }
    this.ollamaModels = []
    this.testResult = null
  }

  onTypeChange() {
    const urls: any = {
      OLLAMA_LOCAL: 'http://localhost:11434',
      OLLAMA_REMOTE: 'http://192.168.1.100:11434',
      CLAUDE: 'https://api.anthropic.com',
      OPENAI: 'https://api.openai.com',
      CUSTOM: '',
    }
    this.form.baseUrl = urls[this.form.type] || ''
    this.form.model = this.form.type.startsWith('OLLAMA') ? 'mistral:7b' : ''
  }

  editProvider(p: any) {
    this.form = {...p}
    this.testResult = null
  }

  async loadProviders() {
    this.$store.commit('loading')
    const res = await api.get(api.chat, 'ai/provider/list')
    this.$store.commit('stopLoading')
    if (res?.data?.providers) {
      this.providers = res.data.providers
    }
  }

  async saveProvider() {
    this.$store.commit('loading')
    const res = await api.post(api.chat, 'ai/provider/edit', this.form)
    this.$store.commit('stopLoading')
    if (res && res.data) {
      (this.$refs as any).providerModal?.hide?.()
      this.$bvModal?.hide?.('providerModal')
      await this.loadProviders()
    }
  }

  async deleteProvider(p: any) {
    if (!confirm('Supprimer ce provider ?')) return
    await api.get(api.chat, 'ai/provider/delete/' + p.id)
    await this.loadProviders()
  }

  async testProvider(p: any) {
    this.testing = p.id
    this.testResult = null
    const res = await api.post(api.chat, 'ai/provider/test/' + p.id, {})
    this.testing = null
    if (res?.data) {
      this.testResult = res.data
    }
  }
}
</script>
